import { type ChangeEvent, type FormEvent, useRef, useState } from "react";
import { FileText, FileUp } from "lucide-react";
import CleButton from "../../../components/CleButton";
import CleModal, {
  CleModalRoot,
  CleModalTrigger,
} from "../../../components/CleModal";
import { useCreateTeaching } from "@/hooks/useTeachings";
import {
  MAX_LESSONS_PER_EVIDENCE,
  isValidLessonCount,
} from "../../../utils/lessonLimits";
import { getCleUploadErrorMessage } from "../../../utils/getCleUploadErrorMessage";

const TEACHING_FORM_ID = "cle-teaching-upload-form";

const TeachingUploadDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [numberOfLessons, setNumberOfLessons] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createTeachingMutation = useCreateTeaching();

  const resetForm = () => {
    setFile(null);
    setNumberOfLessons("");
    setValidationError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      createTeachingMutation.reset();
      setValidationError(null);

      return;
    }

    resetForm();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
    setValidationError(null);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const lessons = Number(numberOfLessons);

    if (!file) {
      setValidationError("Vælg en PDF-fil med dokumentation for undervisning.");

      return;
    }

    if (!isValidLessonCount(lessons)) {
      setValidationError(
        `Angiv et antal lektioner mellem 1 og ${MAX_LESSONS_PER_EVIDENCE}. Decimaler er tilladt og afrundes til to decimaler.`,
      );

      return;
    }

    createTeachingMutation.mutate(
      {
        file,
        numberOfLessons: lessons,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          resetForm();
        },
      },
    );
  };

  return (
    <CleModalRoot
      controller={{
        open: isOpen,
        setOpen: setIsOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <CleModalTrigger asChild>
        <CleButton id="cle-upload-teaching" variant="outline" size="sm">
          <FileUp size={17} strokeWidth={2} />
          Opret undervisning
        </CleButton>
      </CleModalTrigger>
      <CleModal
        title="Opret undervisning"
        description="Upload dokumentation for undervisning og angiv antal lektioner."
        size="sm"
        footer={
          <div className="d-flex justify-content-center gap-3 w-100 px-3 py-2">
            <CleButton
              id="cle-cancel-teaching-upload"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createTeachingMutation.isPending}
            >
              Afbryd
            </CleButton>
            <CleButton
              id="cle-submit-teaching-upload"
              type="submit"
              form={TEACHING_FORM_ID}
              disabled={createTeachingMutation.isPending}
            >
              {createTeachingMutation.isPending ? "Opretter..." : "Opret"}
            </CleButton>
          </div>
        }
      >
        <form
          id={TEACHING_FORM_ID}
          className="d-flex flex-column gap-3"
          onSubmit={handleSubmit}
        >
          <div className="d-flex flex-column align-items-center text-center gap-3">
            <span
              className="cle-compliance-doc-dashboard__empty-icon mb-0"
              aria-hidden="true"
            >
              <FileText size={24} strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="fs-5 fw-semibold mb-2">Upload undervisning</h3>
              <p className="text-muted mb-0">
                Du kan kun uploade PDF-filer. Angiv også hvor mange lektioner
                undervisningen dækker.
              </p>
            </div>
          </div>

          <div className="alert alert-info mb-0" role="note">
            Sekretariatet gennemgår undervisning manuelt. Undervisningen står
            som “Afventer sekretariat”, indtil den er godkendt, og først
            derefter kan lektionerne indgå i en indberetning.
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-teaching-file"
            >
              Dokumentation
            </label>
            <input
              ref={fileInputRef}
              id="cle-teaching-file"
              className="form-control"
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              disabled={createTeachingMutation.isPending}
            />
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-teaching-lessons"
            >
              Antal lektioner
            </label>
            <input
              id="cle-teaching-lessons"
              className="form-control"
              type="number"
              inputMode="decimal"
              min="1"
              max={MAX_LESSONS_PER_EVIDENCE}
              step="any"
              value={numberOfLessons}
              onChange={(event) => {
                setNumberOfLessons(event.target.value);
                setValidationError(null);
              }}
              disabled={createTeachingMutation.isPending}
            />
          </div>

          {validationError ? (
            <div className="alert alert-warning mb-0" role="alert">
              {validationError}
            </div>
          ) : null}

          {createTeachingMutation.isError ? (
            <div className="alert alert-warning mb-0" role="alert">
              {getCleUploadErrorMessage(
                createTeachingMutation.error,
                "Undervisningen kunne ikke oprettes lige nu. Prøv igen.",
              )}
            </div>
          ) : null}
        </form>
      </CleModal>
    </CleModalRoot>
  );
};

export default TeachingUploadDialog;
